import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ArrowLeftRight, CheckCircle2, XCircle, Wallet, Loader2 } from "lucide-react";

/** Accept / counter / decline panel shown when it's the viewer's turn.
 *  Mount with key={booking.id} so counter-offer state resets per booking. */
export const BookingActions = ({ booking, isClient, busy, onRespond }) => {
  const [showCounter, setShowCounter] = useState(false);
  const [counterAmount, setCounterAmount] = useState("");
  const [counterNote, setCounterNote] = useState("");

  const sendCounter = async () => {
    const ok = await onRespond("counter", parseFloat(counterAmount), counterNote);
    if (ok) {
      setShowCounter(false);
      setCounterAmount("");
      setCounterNote("");
    }
  };

  return (
    <div className="space-y-3 rounded-xl border-2 border-[hsl(174,38%,70%)] p-4">
      <p className="text-sm font-semibold">
        Current offer: <span className="font-display text-lg">{fmtMoney(booking.current_offer?.amount)}</span> — your move
      </p>
      <div className="flex flex-wrap gap-2">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button data-testid="booking-accept-button" disabled={busy} className="rounded-xl">
              <CheckCircle2 className="mr-1.5 h-4 w-4" /> Accept {fmtMoney(booking.current_offer?.amount)}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {isClient ? "Accept this counter offer?" : "Consent to this job?"}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {isClient
                  ? `You agree to pay ${fmtMoney(booking.current_offer?.amount)} upfront from your wallet once you complete payment.`
                  : `By accepting, you consent to "${booking.job?.title}" at ${fmtMoney(booking.current_offer?.amount)} paid upfront${
                      booking.job?.retouching_requested
                        ? ", INCLUDING consent to minor retouching (lighting, colour grading, blemish removal only — never reshaping)"
                        : ", with NO retouching consented"
                    }. The client is bound by the Ethical Usage Agreement.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="booking-accept-cancel" className="rounded-xl">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="booking-accept-confirm" className="rounded-xl" onClick={() => onRespond("accept")}>
                {isClient ? "Accept offer" : "I consent — accept"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <Button data-testid="booking-negotiation-counteroffer-button" variant="outline" disabled={busy}
          className="rounded-xl border bg-card hover:bg-muted" onClick={() => setShowCounter(!showCounter)}>
          <ArrowLeftRight className="mr-1.5 h-4 w-4" /> Counter
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button data-testid="booking-decline-button" variant="outline" disabled={busy} className="rounded-xl border bg-card text-destructive hover:bg-muted">
              <XCircle className="mr-1.5 h-4 w-4" /> Decline
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Decline this booking?</AlertDialogTitle>
              <AlertDialogDescription>This ends the negotiation. The other party will be notified.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="booking-decline-confirm" className="rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => onRespond("decline")}>
                Decline booking
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      {showCounter && (
        <div className="space-y-2 rounded-xl bg-muted p-3">
          <Input data-testid="booking-counter-amount-input" type="number" min="1" placeholder="Counter amount (USD)"
            value={counterAmount} onChange={(e) => setCounterAmount(e.target.value)} className="rounded-xl bg-card" />
          <Textarea data-testid="booking-counter-note-input" rows={2} placeholder="Explain your terms (optional)…"
            value={counterNote} onChange={(e) => setCounterNote(e.target.value)} className="rounded-xl bg-card" />
          <Button data-testid="booking-negotiation-send-button" disabled={busy || !counterAmount} className="rounded-xl"
            onClick={sendCounter}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send counter offer
          </Button>
        </div>
      )}
    </div>
  );
};

/** Waiting / pay / active / declined panels for every non-actionable state. */
export const BookingStatusPanels = ({ booking, isClient, myTurn, agreed, wallet, busy, onPay }) => {
  const navigate = useNavigate();
  return (
    <>
      {["pending", "countered"].includes(booking.status) && !myTurn && (
        <div className="rounded-xl bg-muted p-4 text-sm text-muted-foreground">
          Waiting for the {booking.awaiting === "client" ? "client" : "model side"} to respond to {fmtMoney(booking.current_offer?.amount)}…
        </div>
      )}

      {booking.status === "accepted" && isClient && (
        <div className="space-y-3 rounded-xl border-2 border-[hsl(174,38%,70%)] bg-[hsl(174,38%,96%)] p-4">
          <p className="text-sm">
            <span className="font-semibold">{booking.model_name} consented.</span> Pay{" "}
            <span className="font-display text-lg">{fmtMoney(agreed)}</span> upfront to activate your licence.
          </p>
          <p className="text-xs text-muted-foreground">
            Wallet balance: <span className="font-mono-plex font-semibold">{fmtMoney(wallet?.balance)}</span>
            {wallet && wallet.balance < agreed && (
              <> — insufficient. <Link to="/wallet" className="font-semibold underline">Top up your wallet</Link></>
            )}
          </p>
          <Button data-testid="booking-pay-button" disabled={busy || (wallet && wallet.balance < agreed)} onClick={onPay} size="lg"
            className="rounded-xl shadow-[0_10px_25px_rgba(30,26,22,0.12)]">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wallet className="mr-2 h-4 w-4" />}
            Pay {fmtMoney(agreed)} & activate licence
          </Button>
        </div>
      )}

      {booking.status === "accepted" && !isClient && (
        <div className="rounded-xl bg-muted p-4 text-sm text-muted-foreground">
          You consented at {fmtMoney(agreed)}. Waiting for the client's upfront payment — it lands in your wallet the moment they pay.
        </div>
      )}

      {booking.status === "active" && (
        <div className="flex items-center justify-between rounded-xl border-2 border-[hsl(174,38%,70%)] bg-[hsl(174,38%,96%)] p-4">
          <p className="text-sm">
            <span className="font-semibold">Licence active.</span> {fmtMoney(agreed)} paid upfront.
          </p>
          <Button data-testid="booking-view-license-button" variant="outline" className="rounded-xl border bg-card hover:bg-background" onClick={() => navigate("/licenses")}>
            View licence
          </Button>
        </div>
      )}

      {booking.status === "declined" && (
        <div className="rounded-xl bg-muted p-4 text-sm text-muted-foreground">This booking was declined.</div>
      )}
    </>
  );
};
